import { useState, useEffect, useRef } from "react";
import { Upload, FileText, Eye, Download, User, ChevronDown, ChevronRight, X, Check, Trash2, Pencil } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ResponsesView({ members }) {
  const [responses, setResponses] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadMember, setUploadMember] = useState("");
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [expandedMembers, setExpandedMembers] = useState({});
  const fileRef = useRef(null);

  useEffect(() => { loadResponses(); }, []);

  async function loadResponses() {
    try {
      const res = await axios.get(`${API}/responses`);
      setResponses(res.data || []);
    } catch { /* */ }
    finally { setLoaded(true); }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !uploadMember) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("memberName", uploadMember);
      form.append("resourceTitle", uploadTitle || file.name);
      await axios.post(`${API}/responses/upload`, form);
      await loadResponses();
      setShowUpload(false);
      setUploadMember("");
      setUploadTitle("");
    } catch { /* */ }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function viewResponse(r) {
    try {
      const res = await axios.get(`${API}/responses/${r.responseId}`);
      const data = res.data;
      if (data.formData) {
        setPreviewData({ type: "form", formData: data.formData, member: data.memberName, title: data.resourceTitle });
      } else if (data.fileData) {
        if (data.contentType?.startsWith("image/")) {
          setPreviewData({ type: "file", url: data.fileData, contentType: data.contentType });
        } else {
          window.open(`${API}/responses/${r.responseId}/download`, "_blank");
        }
      }
    } catch { /* */ }
  }

  function toggleMember(name) {
    setExpandedMembers(prev => ({ ...prev, [name]: !prev[name] }));
  }

  async function deleteResponse(responseId) {
    if (!window.confirm("Delete this response?")) return;
    try {
      await axios.delete(`${API}/responses/${responseId}`);
      await loadResponses();
    } catch { /* */ }
  }

  const sortedMembers = [...members].sort();

  // Group responses by member name
  const memberNames = [...new Set(responses.map(r => r.memberName))].sort();
  const grouped = {};
  for (const name of memberNames) {
    grouped[name] = responses
      .filter(r => r.memberName === name)
      .sort((a, b) => (b.submittedAt || "").localeCompare(a.submittedAt || ""));
  }

  return (
    <div data-testid="responses-view">
      {/* Preview Modal */}
      {previewData && (
        <div className="fixed inset-0 z-50 bg-black/70" onClick={() => setPreviewData(null)}>
          <button onClick={() => setPreviewData(null)} className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center bg-black/50 text-white rounded-full">
            <X className="w-5 h-5" />
          </button>
          {previewData.type === "file" ? (
            <div className="w-full h-full sm:p-4 sm:flex sm:items-center sm:justify-center" onClick={e => e.stopPropagation()}>
              {previewData.contentType?.startsWith("image/") ? (
                <img src={previewData.url} alt="" className="w-full h-full object-contain sm:max-w-4xl sm:max-h-[90vh] sm:rounded-lg" />
              ) : (
                <iframe src={previewData.url} className="w-full h-full sm:max-w-4xl sm:max-h-[90vh] sm:rounded-lg bg-white" title="Preview" />
              )}
            </div>            ) : previewData.type === "form" ? (
              <div className="w-full h-full sm:flex sm:items-center sm:justify-center sm:p-4" onClick={e => e.stopPropagation()}>
                <div className="bg-white sm:rounded-xl w-full sm:max-w-lg h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto scroll-smooth">
                  <div className="bg-[#0B2A47] text-white px-5 py-4 sm:rounded-t-xl sticky top-0 z-10">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[10px] font-bold tracking-widest uppercase text-[#8FC4E8] mb-1">CHART · {previewData.title}</p>
                        <h3 className="text-base font-bold">{previewData.member}</h3>
                      </div>
                      <button onClick={() => setPreviewData(null)} className="p-1 text-white/60 hover:text-white">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="p-5 space-y-4">
                    {(() => {
                      const entries = Object.entries(previewData.formData);
                      const groups = [];
                      let currentGroup = null;
                      entries.forEach(([key, val]) => {
                        const match = key.match(/^(.+?)\s*#\d+$/);
                        const groupName = match ? match[1] : null;
                        if (groupName && currentGroup?.name === groupName) {
                          currentGroup.items.push({ key, val });
                        } else if (groupName) {
                          currentGroup = { name: groupName, items: [{ key, val }] };
                          groups.push(currentGroup);
                        } else {
                          currentGroup = null;
                          groups.push({ name: null, items: [{ key, val }] });
                        }
                      });
                      return groups.map((group, gi) => {
                        if (group.name) {
                          const filled = group.items.filter(i => i.val);
                          if (filled.length === 0) return null;
                          return (
                            <div key={gi}>
                              <p className="text-[10px] font-bold text-[#0B2A47] tracking-widest uppercase mb-2 border-b border-zinc-200 pb-1">{group.name}</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                {filled.map(({ key, val }) => {
                                  const num = key.match(/#(\d+)$/)?.[1];
                                  return (
                                    <p key={key} className="text-sm text-zinc-700">
                                      <span className="text-zinc-400 text-xs mr-1">{num}.</span> {val}
                                    </p>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        return group.items.map(({ key, val }) => {
                          if (!val) return null;
                          return (
                            <div key={key}>
                              <p className="text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-0.5">{key}</p>
                              <p className="text-sm text-zinc-800 leading-relaxed">{val}</p>
                            </div>
                          );
                        });
                      });
                    })()}
                  </div>
                </div>
              </div>
            ) : null}
        </div>
      )}

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-base font-bold text-zinc-900">Member Responses</h3>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-zinc-900 hover:bg-zinc-800 rounded-lg transition-colors"
        >
          <Upload className="w-3.5 h-3.5" /> Upload Response
        </button>
      </div>

      {/* Upload Form */}
      {showUpload && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1">Member Name *</label>
              <select value={uploadMember} onChange={e => setUploadMember(e.target.value)} className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500">
                <option value="">Select member</option>
                {sortedMembers.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1">Document Title</label>
              <input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" placeholder="e.g. 1-on-1 Guide Completed" />
            </div>
          </div>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt" onChange={handleUpload} className="hidden" />
            <button
              onClick={() => { if (uploadMember) fileRef.current?.click(); }}
              disabled={uploading || !uploadMember}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Choose File & Upload"}
            </button>
            <button onClick={() => setShowUpload(false)} className="px-3 py-2.5 text-xs font-bold text-zinc-600 bg-zinc-200 hover:bg-zinc-300 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Responses grouped by member */}
      {!loaded ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-3 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      ) : memberNames.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">No responses submitted yet.</p>
          <p className="text-xs text-zinc-300 mt-1">Members can fill out forms or upload completed worksheets.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {memberNames.map(name => {
            const items = grouped[name];
            const isOpen = expandedMembers[name] !== false; // default open
            return (
              <div key={name} className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
                <button
                  onClick={() => toggleMember(name)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-sm font-bold text-zinc-900 flex-1">{name}</span>
                  <span className="text-[11px] font-bold text-zinc-400">{items.length} response{items.length !== 1 ? "s" : ""}</span>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
                </button>
                {isOpen && (
                  <div className="border-t border-zinc-100 divide-y divide-zinc-50">
                    {items.map(r => (
                      <div key={r.responseId} className="px-4 py-2.5 pl-14">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-zinc-700 truncate">{r.resourceTitle || r.fileName || "Response"}</p>
                            <div className="flex items-center gap-2 text-[10px] text-zinc-400">
                              {r.formData && <span className="text-orange-500 font-bold">Filled in system</span>}
                              {r.fileSize && <span>{formatSize(r.fileSize)}</span>}
                              <span>{new Date(r.submittedAt).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 mt-1.5">
                          <button onClick={() => viewResponse(r)} className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 text-[11px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors">
                            <Eye className="w-3.5 h-3.5" /> View
                          </button>
                          {r.fileSize && (
                            <a href={`${API}/responses/${r.responseId}/download`} className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 text-[11px] font-bold text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors">
                              <Download className="w-3.5 h-3.5" /> Save
                            </a>
                          )}
                          <button onClick={() => deleteResponse(r.responseId)} className="p-1.5 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
